import { type FC, useCallback, useMemo, useRef, useState } from "react";

import { SwitchEntityTypes } from "enums/switchEntityTypes";
import { TemplateTypes } from "../../../enums";
import { getNotifyConfig } from "../../../features/SystemNotifications/helpers";
import { NotifyStepConfig } from "../../../features/SystemNotifications/types";
import { type NotifyStepSettings, type IObservable } from "../../../interfaces";
import { type NotifyByEmailController } from "../../notifyStep/notifyByEmailController";
import { ModalBanner } from "./ModalBanner/ModalBanner";

import { tabs } from "components/notifyStep/NotifyStepSwitch/constants";
import ModalSizes from "../../../enums/modalSizes";
import useNotifyConfig from "../../../features/SystemNotifications/hooks/useNotifyConfig";
import { ButtonGroup } from "../../buttonGroup";
import NotifyWizardStep from "../../notifyStep/NotifyWizardStep/NotifyWizardStep";
import TriggerableConfirmationModal from "../../triggeredConfirmationModal/TriggerableConfirmationModal";

export interface PublishConfirmationModalProps {
  onTriggerPublishContentObserver: IObservable<(onConfirm: () => void, entityState: EntityState) => void>;
  onConfirmAction: (entityId: number, notificationSettings?: NotifyStepSettings, notifyTypes?: number[]) => void;
  dependencyType?: string;
  contentType?: string;
  labelText?: string;
  onClose?: () => void;
}

export interface EntityState {
  itemsCount: number;
  entityId: number;
  hasBeenPublished: boolean;
  templateType: TemplateTypes;
  notifyConfigType?: NotifyStepConfig;
  entityType?: SwitchEntityTypes;
}

export const PublishConfirmationModal: FC<PublishConfirmationModalProps> = (props) => {
  const controllerRef = useRef<NotifyByEmailController>();
  const [isDataValid, setIsDataValid] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [entityState, setEntityState] = useState<EntityState>();
  const { contentType, dependencyType, labelText } = props;
  const { config, NotifyConfigSwitch } = getNotifyConfig(entityState?.notifyConfigType || NotifyStepConfig.Default);
  const [notifyConfig, { setNotifyConfig, resetNotifyConfig, shouldNotify, communicationChannel, notifyTypes }] =
    useNotifyConfig<any>(config);

  const handleCommunicationChannelChange = useCallback(
    (value: string) =>
      setNotifyConfig({
        key: "communicationChannel",
        value: tabs.findIndex((name) => name === value),
      }),
    [setNotifyConfig],
  );

  const onNotifyStepLoaded = (controller: NotifyByEmailController) => {
    controllerRef.current = controller;
    setIsDataLoaded(true);
  };

  const onResetNotifyStep = () => {
    controllerRef.current?.resetState();
    resetNotifyConfig();
    props.onClose?.();
  };

  const onConfirmed = async () => {
    const notificationSettings = await controllerRef.current?.getSettings(
      shouldNotify,
      communicationChannel,
      !entityState?.hasBeenPublished,
    );
    props.onConfirmAction(entityState!.entityId, notificationSettings, notifyTypes);
    onResetNotifyStep();
  };

  const modalContent = useMemo(() => {
    return (
      <NotifyWizardStep
        templateType={entityState?.templateType || TemplateTypes.Empty}
        onLoaded={onNotifyStepLoaded}
        onValidChange={setIsDataValid}
        isDisabled={!entityState?.hasBeenPublished}
        shouldNotify={shouldNotify}
        communicationChannel={communicationChannel}
        renderSwitch={(switchProps) => (
          <div>
            <NotifyConfigSwitch
              config={notifyConfig}
              labelText={labelText}
              onNotifyConfigChange={setNotifyConfig}
              entityType={entityState?.entityType || SwitchEntityTypes.Empty}
              {...switchProps}
            />
            {entityState?.notifyConfigType === NotifyStepConfig.WithProgress && (
              <ButtonGroup
                items={tabs}
                defaultValue={tabs[communicationChannel]}
                isDisabled={
                  !notifyConfig.shouldNotifyNotStarted &&
                  !notifyConfig.shouldNotifyInProgress &&
                  !notifyConfig.shouldNotifyCompleted
                }
                onChange={handleCommunicationChannelChange}
              />
            )}
          </div>
        )}
      />
    );
  }, [
    entityState?.templateType,
    entityState?.hasBeenPublished,
    entityState?.entityType,
    entityState?.notifyConfigType,
    shouldNotify,
    communicationChannel,
    NotifyConfigSwitch,
    notifyConfig,
    labelText,
    setNotifyConfig,
    handleCommunicationChannelChange,
  ]);

  const bannerContent =
    entityState?.itemsCount && contentType && dependencyType ? (
      <ModalBanner
        contentType={contentType}
        dependencyType={dependencyType}
        dependencyCount={entityState?.itemsCount}
      />
    ) : undefined;

  // Align disabled logic structure with ContentAssignmentModalTwoSteps
  const notifyStepNotReady = !(isDataValid && isDataLoaded);
  const needNotification = shouldNotify;

  return (
    <TriggerableConfirmationModal
      title="Publish"
      size={ModalSizes.large}
      confirmButtonLabel="Finish"
      content={modalContent}
      banner={bannerContent}
      disabled={needNotification && notifyStepNotReady}
      onTriggerModalObserver={props.onTriggerPublishContentObserver}
      onTrigger={setEntityState}
      onConfirmed={onConfirmed}
      onClose={onResetNotifyStep}
    />
  );
};

export default PublishConfirmationModal;
